import { createId } from "./ops";
import {
  TABLE_CELL_MAX_LENGTH,
  TABLE_MAX_COLUMNS,
  TABLE_MAX_ROWS,
  type TableColumn,
  type TableElement,
} from "./schema";

/**
 * Tables: factory, presets, row/column editing, paste parsing ("Aus Excel
 * einfügen") and a height estimate. Editing helpers mutate (Immer drafts).
 */

export type TableAlign = TableColumn["align"];

export function createTableElement(
  input: Partial<Omit<TableElement, "type">> & { cells?: string[][] } = {},
): TableElement {
  const cells = input.cells ?? [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""],
  ];
  const width = Math.max(1, ...cells.map((row) => row.length));
  const element: TableElement = {
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    id: input.id ?? createId("table"),
    type: "table",
    x: 0,
    y: 0,
    width: 800,
    height: 300,
    cells,
    columns: Array.from({ length: width }, () => ({ width: 1, align: "left" as const })),
    header: true,
    zebra: false,
    fontFamily: "Inter",
    fontSize: 28,
    fontWeight: 400,
    lineHeight: 1.3,
    color: "#111111",
    headerColor: "#ffffff",
    headerFill: "#111827",
    bodyFill: "#ffffff00",
    stripeFill: "#f4f4f5",
    borderColor: "#d4d4d8",
    borderWidth: 2,
    padding: 16,
    rowHeight: 0,
    ...input,
  };
  normalizeTable(element);
  element.height = input.height ?? estimateTableHeight(element);
  return element;
}

/** Makes every row as long as `columns` and keeps the limits (mutating). */
export function normalizeTable(element: TableElement): void {
  if (element.columns.length > TABLE_MAX_COLUMNS) element.columns.length = TABLE_MAX_COLUMNS;
  if (element.cells.length > TABLE_MAX_ROWS) element.cells.length = TABLE_MAX_ROWS;
  if (element.cells.length === 0) element.cells.push([]);
  const count = element.columns.length;
  element.cells.forEach((row, index) => {
    if (row.length > count) row.length = count;
    while (row.length < count) row.push("");
    element.cells[index] = row.map((cell) => cell.slice(0, TABLE_CELL_MAX_LENGTH));
  });
}

export function cellAt(element: TableElement, row: number, column: number): string {
  return element.cells[row]?.[column] ?? "";
}

export function insertTableRow(element: TableElement, at = element.cells.length): boolean {
  if (element.cells.length >= TABLE_MAX_ROWS) return false;
  element.cells.splice(
    at,
    0,
    element.columns.map(() => ""),
  );
  return true;
}

export function removeTableRow(element: TableElement, at: number): boolean {
  if (element.cells.length <= 1 || !element.cells[at]) return false;
  element.cells.splice(at, 1);
  return true;
}

export function insertTableColumn(element: TableElement, at = element.columns.length): boolean {
  if (element.columns.length >= TABLE_MAX_COLUMNS) return false;
  const width =
    element.columns.reduce((sum, column) => sum + column.width, 0) / element.columns.length || 1;
  element.columns.splice(at, 0, { width, align: "left" });
  for (const row of element.cells) row.splice(at, 0, "");
  return true;
}

export function removeTableColumn(element: TableElement, at: number): boolean {
  if (element.columns.length <= 1 || !element.columns[at]) return false;
  element.columns.splice(at, 1);
  for (const row of element.cells) row.splice(at, 1);
  return true;
}

/** Column widths as fractions of the table width (sum 1). */
export function columnFractions(element: TableElement): number[] {
  const total = element.columns.reduce((sum, column) => sum + column.width, 0) || 1;
  return element.columns.map((column) => column.width / total);
}

/** Approximate height (average glyph width ~0.55 em); the editor measures the real one. */
export function estimateTableHeight(element: TableElement): number {
  const fractions = columnFractions(element);
  const line = element.fontSize * element.lineHeight;
  let height = element.borderWidth;
  for (const row of element.cells) {
    let lines = 1;
    row.forEach((cell, index) => {
      const inner = Math.max(
        element.fontSize,
        element.width * (fractions[index] ?? 0) - 2 * element.padding,
      );
      const perLine = Math.max(1, Math.floor(inner / (element.fontSize * 0.55)));
      const count = cell
        .split("\n")
        .reduce((sum, part) => sum + Math.max(1, Math.ceil(part.length / perLine)), 0);
      lines = Math.max(lines, count);
    });
    height += Math.max(element.rowHeight, lines * line + 2 * element.padding) + element.borderWidth;
  }
  return Math.max(1, Math.round(height));
}

/** Text of the table, for labels and search ("Produkt · Preis …"). */
export function tableSummary(element: TableElement, maxLength = 28): string {
  const text = element.cells
    .flat()
    .map((cell) => cell.trim())
    .filter(Boolean)
    .join(" · ");
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

// ---------------------------------------------------------------------------
// Pasting from Excel, Numbers, Google Sheets or CSV files
// ---------------------------------------------------------------------------

/** Tab (spreadsheets), semicolon (German CSV) or comma, by frequency in the first line. */
export function detectDelimiter(text: string): "\t" | ";" | "," {
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const count = (char: string) => first.split(char).length - 1;
  if (count("\t") > 0) return "\t";
  return count(";") >= count(",") && count(";") > 0 ? ";" : ",";
}

/**
 * Parses TSV/CSV text into rows of cells: quoted fields (with `""` and line
 * breaks inside), CRLF, trailing empty lines. Cells are trimmed; the result is
 * rectangular and within the table limits.
 */
export function parseTableText(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  let index = 0;
  const source = text.replace(/\r\n?/g, "\n");
  while (index < source.length) {
    const char = source[index]!;
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index++;
        } else quoted = false;
      } else cell += char;
    } else if (char === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (char === delimiter) {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += char;
    index++;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  const trimmed = rows
    .map((cells) => cells.map((value) => value.trim().slice(0, TABLE_CELL_MAX_LENGTH)))
    .filter((cells) => cells.some((value) => value !== ""))
    .slice(0, TABLE_MAX_ROWS);
  const width = Math.min(TABLE_MAX_COLUMNS, Math.max(0, ...trimmed.map((cells) => cells.length)));
  return trimmed.map((cells) => {
    const out = cells.slice(0, width);
    while (out.length < width) out.push("");
    return out;
  });
}

/**
 * Number in German or English notation ("1.234,50 €", "12,5 %", "-3", "9.99"),
 * or null. Used to right-align number columns.
 */
export function parseCellNumber(value: string): number | null {
  const cleaned = value
    .replace(/[\s €$£%]|EUR|CHF/gi, "")
    .replace(/^\+/, "")
    .trim();
  if (!/^-?[\d.,]+$/.test(cleaned) || !/\d/.test(cleaned)) return null;
  let normalized = cleaned;
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  if (lastComma > lastDot) {
    // German: dots group thousands, the comma is the decimal separator.
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma && lastComma !== -1) {
    normalized = cleaned.replace(/,/g, "");
  } else if (lastDot !== -1 && /^-?\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    // "1.234" is a German thousand, not 1.234.
    normalized = cleaned.replace(/\./g, "");
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

/** Suggested alignment per column: right where all body cells are numbers. */
export function suggestAlignments(cells: string[][], header: boolean): TableAlign[] {
  const width = Math.max(0, ...cells.map((row) => row.length));
  const body = header ? cells.slice(1) : cells;
  return Array.from({ length: width }, (_, column) => {
    const values = body.map((row) => row[column] ?? "").filter((value) => value.trim() !== "");
    return values.length > 0 && values.every((value) => parseCellNumber(value) !== null)
      ? "right"
      : "left";
  });
}

/** Replaces the content with pasted rows (columns and alignments follow). Mutating. */
export function applyPastedTable(element: TableElement, rows: string[][]): boolean {
  if (rows.length === 0 || rows[0]!.length === 0) return false;
  const aligns = suggestAlignments(rows, element.header);
  // Same number of columns: keep the widths; otherwise start with equal ones.
  const keepWidths = aligns.length === element.columns.length;
  element.cells = rows.map((row) => [...row]);
  element.columns = aligns.map((align, index) => ({
    width: keepWidths ? (element.columns[index]?.width ?? 1) : 1,
    align,
  }));
  normalizeTable(element);
  return true;
}

// ---------------------------------------------------------------------------
// Presets of the elements panel
// ---------------------------------------------------------------------------

export interface TablePreset {
  id: string;
  label: string;
  create: () => TableElement;
}

export const TABLE_PRESETS: readonly TablePreset[] = [
  {
    id: "3x3",
    label: "3 × 3",
    create: () =>
      createTableElement({
        header: false,
        cells: [
          ["", "", ""],
          ["", "", ""],
          ["", "", ""],
        ],
      }),
  },
  {
    id: "header-4x5",
    label: "4 × 5 mit Kopfzeile",
    create: () =>
      createTableElement({
        header: true,
        zebra: true,
        cells: [
          ["Spalte 1", "Spalte 2", "Spalte 3", "Spalte 4"],
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
          ["", "", "", ""],
        ],
      }),
  },
  {
    id: "price-list",
    label: "Preisliste",
    create: () => {
      const element = createTableElement({
        header: true,
        cells: [
          ["Leistung", "Preis"],
          ["Beratung (60 Min.)", "90,00 €"],
          ["Workshop halbtags", "450,00 €"],
          ["Workshop ganztags", "790,00 €"],
        ],
        borderWidth: 0,
        zebra: true,
      });
      element.columns = [
        { width: 3, align: "left" },
        { width: 1, align: "right" },
      ];
      element.height = estimateTableHeight(element);
      return element;
    },
  },
];
